import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { apiPath } from 'apiurl';
import diagnosticsEnabled from 'utils/diagnosticsEnabled';

function getPipelineKey(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    const p = parsed.pathname;
    const tr = parsed.searchParams.get('timeRange') || 'all';

    if (p.includes('/api/datasize'))                          return 'datasize';
    if (p.includes('/api/processedrun/stats'))                return `stats-${tr}`;
    if (p.includes('/api/dashboard-status/summary'))          return `summary-${tr}`;
    if (p.includes('/api/dashboard-status/query-status'))     return `query-status-${tr}`;
    if (p.includes('/api/dashboard-status/metadata-counts'))  return 'metadata-counts';
    if (p.includes('/api/dashboard-status/cadence-counts'))    return 'cadence-counts';
    if (p.includes('/api/dashboard-status/cadence-timeline')) return `cadence-timeline-${tr}`;
    if (p.includes('/api/dashboard-status/cadence-details'))  return 'cadence-details';
  } catch {}
  return null;
}

function WidgetDiagnostics({ timing, url, dataCount, panelPlacement = 'below' }) {
  const [open, setOpen]               = useState(false);
  const [copiedUrl, setCopiedUrl]     = useState(false);
  const [queryState, setQuery]         = useState({ status: 'idle', json: null, error: null });
  const [panelStyle, setPanelStyle]    = useState(null);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const updatePanelPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const gap = 8;
    const margin = 12;
    const panelWidth = 260;
    const panelHeight = panelRef.current?.offsetHeight || 300;
    const rect = buttonRef.current.getBoundingClientRect();
    const preferredAbove = panelPlacement === 'above';
    const hasRoomAbove = rect.top >= panelHeight + gap + margin;
    const hasRoomBelow = window.innerHeight - rect.bottom >= panelHeight + gap + margin;
    const placeAbove = preferredAbove ? hasRoomAbove || !hasRoomBelow : !hasRoomBelow && hasRoomAbove;

    setPanelStyle({
      left: Math.max(margin, Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - margin)),
      top: placeAbove ? Math.max(margin, rect.top - panelHeight - gap) : Math.min(rect.bottom + gap, window.innerHeight - panelHeight - margin),
    });
  }, [panelPlacement]);

  useEffect(() => {
    if (!open) return;
    updatePanelPosition();

    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (open) updatePanelPosition();
  }, [open, queryState.status, updatePanelPosition]);

  if (!diagnosticsEnabled) return null;
  if (!timing) return null;

  const endpoint = url
    ? url.includes('/api/') ? `/api/${url.split('/api/')[1]}` : url
    : '—';

  const clientSource = timing.fromCache
    ? `Browser cache${timing.ageMs != null ? ` · ${Math.round(timing.ageMs / 1000)}s old` : ''}`
    : 'Network';

  const serverCacheClass =
    timing.cacheStatus === 'HIT'   ? 'wd-cache' :
    timing.cacheStatus === 'STALE' ? 'wd-stale' : 'wd-network';

  const handleCopyUrl = async () => {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(url || '');
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 1500);
    } catch {}
  };

  const handleCopyQuery = async () => {
    const key = getPipelineKey(url);
    if (!key) return;

    setQuery({ status: 'loading', json: null, error: null });
    try {
      const res = await axios.get(`${apiPath}/api/debug/pipeline`, { params: { name: key } });
      const pipeline = res.data?.pipeline;
      if (pipeline == null) {
        setQuery({ status: 'error', json: null, error: 'No pipeline data — reload the widget first' });
        setTimeout(() => setQuery({ status: 'idle', json: null, error: null }), 3000);
        return;
      }
      if (!navigator.clipboard) throw new Error('Clipboard API not available (requires HTTPS)');
      const json = JSON.stringify(pipeline, null, 2);
      await navigator.clipboard.writeText(json);
      setQuery({ status: 'copied', json, error: null });
      setTimeout(() => setQuery(s => ({ ...s, status: 'idle' })), 2000);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed';
      setQuery({ status: 'error', json: null, error: msg });
      setTimeout(() => setQuery({ status: 'idle', json: null, error: null }), 3000);
    }
  };

  const queryLabel =
    queryState.status === 'loading' ? '…'        :
    queryState.status === 'copied'  ? '✓ Copied'  :
    queryState.status === 'error'   ? '✗ Error'   : 'Copy Query';

  const panel = open ? createPortal(
    <div className="wd-panel" ref={panelRef} style={{ ...panelStyle, visibility: panelStyle ? 'visible' : 'hidden' }}>
          <div className="wd-panel-title">Query Diagnostics</div>

          <div className="wd-row">
            <span className="wd-key">Endpoint</span>
            <span className="wd-val wd-val--mono" title={url}>{endpoint}</span>
          </div>

          <div className="wd-divider" />

          <div className="wd-section-label">Timing</div>
          <div className="wd-row">
            <span className="wd-key">Total (client)</span>
            <span className="wd-val wd-val--highlight">{timing.totalMs} ms</span>
          </div>
          {timing.serverMs != null && (
            <div className="wd-row">
              <span className="wd-key">API response</span>
              <span className="wd-val">{timing.serverMs} ms</span>
            </div>
          )}
          {timing.dbMs != null && (
            <div className="wd-row">
              <span className="wd-key">DB query</span>
              <span className="wd-val">{timing.dbMs} ms</span>
            </div>
          )}
          {timing.serverMs != null && timing.dbMs != null && (
            <div className="wd-row">
              <span className="wd-key">Server overhead</span>
              <span className="wd-val">{Math.max(0, timing.serverMs - timing.dbMs)} ms</span>
            </div>
          )}

          <div className="wd-divider" />

          <div className="wd-section-label">Cache</div>
          <div className="wd-row">
            <span className="wd-key">Client</span>
            <span className={`wd-val ${timing.fromCache ? 'wd-cache' : 'wd-network'}`}>{clientSource}</span>
          </div>
          {timing.cacheStatus && (
            <div className="wd-row">
              <span className="wd-key">Server</span>
              <span className={`wd-val ${serverCacheClass}`}>{timing.cacheStatus}</span>
            </div>
          )}

          {dataCount != null && (
            <>
              <div className="wd-divider" />
              <div className="wd-row">
                <span className="wd-key">Records</span>
                <span className="wd-val">{Number(dataCount).toLocaleString()}</span>
              </div>
            </>
          )}

          <div className="wd-divider" />
          <div className="wd-actions">
            <button className="wd-action-btn" onClick={handleCopyUrl}>
              {copiedUrl ? '✓ Copied' : 'Copy URL'}
            </button>
            {getPipelineKey(url) && (
              <button
                className={`wd-action-btn ${queryState.status === 'error' ? 'wd-action-btn--error' : ''}`}
                onClick={handleCopyQuery}
                disabled={queryState.status === 'loading'}
              >
                {queryLabel}
              </button>
            )}
          </div>
          {queryState.status === 'error' && (
            <div className="wd-error-msg">{queryState.error}</div>
          )}
        </div>,
    document.body
  ) : null;

  return (
    <div className={`wd-wrap wd-wrap--${panelPlacement}`} ref={wrapRef}>
      <button
        ref={buttonRef}
        className={`wd-btn${open ? ' wd-btn--active' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Query diagnostics"
        aria-label="Toggle diagnostics"
      >
        ⚡
      </button>
      {panel}
    </div>
  );
}

export default WidgetDiagnostics;
